import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";
import { NotebookPen } from "lucide-react";

import Button from "../../../../../shared/ui/button/Button";
import NotionEditor from "../../../../../shared/ui/form/input/NotionEditor";
import { blockNoteToHtml } from "../../../../../shared/utils/blockNote";
import { sessionService } from "../../../application/SessionApplicationService";

interface SessionPreparationProps {
    sessionId: string;
    preparation: string;
    onSaved: (preparation: string) => void;
}

/**
 * What the therapist or mentor writes ahead of a session to prepare it, in
 * the same editor as the notes. Only ever theirs: the patient never reads it
 * and no model is sent it.
 */
export default function SessionPreparation({ sessionId, preparation, onSaved }: SessionPreparationProps) {
    const { t } = useTranslation();
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(preparation);
    const [saving, setSaving] = useState(false);
    const [saveFailed, setSaveFailed] = useState(false);

    const html = useMemo(() => DOMPurify.sanitize(blockNoteToHtml(preparation)), [preparation]);

    const startEditing = () => {
        setDraft(preparation);
        setSaveFailed(false);
        setEditing(true);
    };

    const save = async () => {
        setSaving(true);
        setSaveFailed(false);
        try {
            await sessionService.updateSession(sessionId, { preparation: draft });
            onSaved(draft);
            setEditing(false);
        } catch {
            setSaveFailed(true);
        } finally {
            setSaving(false);
        }
    };

    return (
        <section
            aria-labelledby="session-preparation-title"
            className="glass-effect glass-border border rounded-2xl p-8 shadow-theme-sm transition-all hover:shadow-theme-md"
        >
            <div className="flex justify-between items-center gap-4 mb-2">
                <h3
                    id="session-preparation-title"
                    className="text-lg font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2"
                >
                    <NotebookPen className="w-4 h-4 text-brand-500" aria-hidden="true" />
                    {t("session.preparation.title")}
                </h3>
                {!editing && (
                    <Button
                        size="sm"
                        variant="outline"
                        className="text-content-tertiary hover:text-brand-500 hover:bg-brand-50"
                        onClick={startEditing}
                    >
                        {t("common.edit")}
                    </Button>
                )}
            </div>
            <p className="mb-5 text-xs text-content-tertiary">{t("session.preparation.privateHint")}</p>

            {editing ? (
                <div className="space-y-4">
                    <NotionEditor
                        value={draft}
                        onChange={setDraft}
                        placeholder={t("session.preparation.placeholder")}
                        className="min-h-[160px]"
                    />
                    {saveFailed && (
                        <p role="alert" className="text-sm text-red-600 dark:text-red-400">
                            {t("session.preparation.saveFailed")}
                        </p>
                    )}
                    <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => setEditing(false)} disabled={saving}>
                            {t("common.cancel")}
                        </Button>
                        <Button size="sm" onClick={save} disabled={saving}>
                            {saving ? t("common.saving") : t("common.save")}
                        </Button>
                    </div>
                </div>
            ) : html.trim() ? (
                <div className="notion-viewer notion-viewer-content" dangerouslySetInnerHTML={{ __html: html }} />
            ) : (
                <p className="text-content-tertiary italic text-sm">{t("session.preparation.empty")}</p>
            )}
        </section>
    );
}
